import { getPageApiBaseUrl } from '@/core/utils/apiUtils';
import {
  PLP_DEFAULT_SORT,
  PLP_LISTING_API_PATH,
  PLP_LISTING_FILTERS_API_PATH,
  PLP_SEO_DATA_API_PATH,
  PLP_PAGE_TYPE,
  type PlpPageType,
} from '../../constants';

export type PlpListingRequestArgs = {
  pageNumber?: number;
  tags?: string;
  /** Base route tag for filter/SEO APIs (`tags_to_sort_filters`). */
  tags_to_sort_filters?: string;
  sortBy?: string;
  preferredProducts?: string;
  is_filtered?: boolean;
  storeIds?: string;
  sid?: string;
  /** Store browse only — from catalog URL slug (old `/page/store-browse`). */
  sidUrl?: string;
  /** Store browse only — 0 city catalog, 1 single store, 2 multi-store (old store API). */
  sidType?: number;
  wid?: string;
  page_type: PlpPageType;
  search_query?: string;
  /** Raw query string without leading `?` (e.g. `search_query=ring&sortby=date_add`). */
  url_query_string?: string;
  is_for_filter_change?: boolean;
  design_ids?: string;
  customer_code?: string;
};

function normalizeQueryValue(value: string): string {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current;
}

export function buildUrlWithQueryParams(baseUrl: string, payload: Record<string, unknown>): string {
  const queryParts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(normalizeQueryValue(String(item)))}`);
      }
    } else {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(normalizeQueryValue(String(value)))}`);
    }
  }
  const qs = queryParts.join('&');
  return qs ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${qs}` : baseUrl;
}

function normaliseTagSpaces(value: string): string {
  return value.replace(/ /g, '+');
}

function trimOptional(value: string | undefined): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || undefined;
}

type SharedContextOptions = {
  /** Browse/store: old `fetchMoreDesigns` omitted `is_filtered` after page 1. */
  includeIsFiltered?: boolean;
};

function buildSharedContextParams(
  args: PlpListingRequestArgs,
  options: SharedContextOptions = {}
): Record<string, unknown> {
  const tags = trimOptional(args.tags);
  const storeIds = trimOptional(args.storeIds);
  const sid = trimOptional(args.sid);
  const sidUrl = trimOptional(args.sidUrl);
  const wid = trimOptional(args.wid);
  const urlQueryString = trimOptional(args.url_query_string);
  const designIds = trimOptional(args.design_ids);
  const searchQuery = trimOptional(args.search_query);
  const isStore = args.page_type === PLP_PAGE_TYPE.STORE;
  const includeIsFiltered = options.includeIsFiltered ?? true;

  return {
    page_type: args.page_type,
    // All page types forward `tags` as-is — omitted when empty (matching old API behaviour
    // where /page/browse, /page/search and /page/store-browse all passed tagString through
    // and callers supplied '' / undefined when no filter was active).
    ...(tags ? { tags: normaliseTagSpaces(tags) } : {}),
    ...(includeIsFiltered && args.is_filtered != null ? { is_filtered: args.is_filtered } : {}),
    ...(storeIds ? { storeIds } : {}),
    ...(sid ? { sid } : {}),
    ...(isStore && sidUrl ? { sidUrl } : {}),
    ...(isStore && args.sidType != null ? { sidType: args.sidType } : {}),
    ...(wid ? { wid } : {}),
    ...(urlQueryString ? { url_query_string: urlQueryString } : {}),
    ...(designIds ? { design_ids: designIds } : {}),
    ...(searchQuery ? { search_query: searchQuery } : {}),
  };
}

/** GET /listing — params aligned with old `/page/browse`, `/page/search`, `/page/store-browse`. */
export function buildPlpListingRequestParams(args: PlpListingRequestArgs): Record<string, unknown> {
  const preferredProducts = trimOptional(args.preferredProducts);
  const customerCode = trimOptional(args.customer_code);
  const isSearch = args.page_type === PLP_PAGE_TYPE.SEARCH;
  const pageNumber = args.pageNumber ?? 1;

  return {
    ...buildSharedContextParams(args, {
      // Old browse/store first page sent is_filtered; pagination did not.
      includeIsFiltered: pageNumber === 1,
    }),
    page_number: pageNumber,
    sortby: args.sortBy ?? PLP_DEFAULT_SORT,
    ...(customerCode ? { customer_code: customerCode } : {}),
    // Old search API did not send preferred_products.
    ...(!isSearch ? { preferred_products: preferredProducts ?? '' } : {}),
    ...(isSearch && preferredProducts ? { preferred_products: preferredProducts } : {}),
  };
}

/** GET /listing/filters */
export function buildPlpListingFiltersRequestParams(args: PlpListingRequestArgs): Record<string, unknown> {
  const tagsToSort = trimOptional(args.tags_to_sort_filters);
  const sidUrl = trimOptional(args.sidUrl);
  const isStore = args.page_type === PLP_PAGE_TYPE.STORE;
  return {
    ...buildSharedContextParams(args),
    ...(tagsToSort ? { tags_to_sort_filters: normaliseTagSpaces(tagsToSort) } : {}),
    is_for_filter_change: args.is_for_filter_change ?? false,
    // Old /page/store-browse-filters sent urlPrefix (the store slug); /listing/filters keeps
    // this param name while /listing uses sidUrl.
    ...(isStore && sidUrl ? { urlPrefix: sidUrl } : {}),
  };
}

/** GET /seo-data */
export function buildPlpSeoDataRequestParams(args: PlpListingRequestArgs): Record<string, unknown> {
  const tagsToSort = trimOptional(args.tags_to_sort_filters);
  const sidUrl = trimOptional(args.sidUrl);
  const isStore = args.page_type === PLP_PAGE_TYPE.STORE;
  return {
    ...buildSharedContextParams(args),
    ...(tagsToSort ? { tags_to_sort_filters: normaliseTagSpaces(tagsToSort) } : {}),
    ...(args.is_for_filter_change != null ? { is_for_filter_change: args.is_for_filter_change } : {}),
    ...(isStore && sidUrl ? { urlPrefix: sidUrl } : {}),
  };
}

function buildListingApiUrl(
  path: string,
  buildParams: (args: PlpListingRequestArgs) => Record<string, unknown>,
  args: PlpListingRequestArgs
): string {
  const base = `${getPageApiBaseUrl()}/page${path}`;
  return buildUrlWithQueryParams(base, buildParams(args));
}

export function buildListingUrl(args: PlpListingRequestArgs): string {
  return buildListingApiUrl(PLP_LISTING_API_PATH, buildPlpListingRequestParams, args);
}

export function buildListingFiltersUrl(args: PlpListingRequestArgs): string {
  return buildListingApiUrl(PLP_LISTING_FILTERS_API_PATH, buildPlpListingFiltersRequestParams, args);
}

export function buildSeoDataUrl(args: PlpListingRequestArgs): string {
  return buildListingApiUrl(PLP_SEO_DATA_API_PATH, buildPlpSeoDataRequestParams, args);
}

/** @deprecated Use buildListingUrl — kept for SSR logging compatibility */
export function buildPlpFullUrl(args: Omit<PlpListingRequestArgs, 'page_type'> & { page_type?: PlpPageType }): string {
  return buildListingUrl({
    ...args,
    page_type: args.page_type ?? 'browse',
  });
}
